import * as THREE from 'three'

// import CustomKTX2Loader from 'Renderer/CustomKTX2Loader'
import CustomGLTFLoader from 'Renderer/CustomGLTFLoader'
import CustomDRACOLoader from 'Renderer/CustomDracoLoader'

class FurnitureLoader {
    constructor() {
        this.gltfLoader = new CustomGLTFLoader()
        // this.ktx2Loader = new CustomKTX2Loader().setTranscoderPath(`${CDN_BUCKET}/lib/basis/`)
        // this.gltfLoader.setKTX2Loader(this.ktx2Loader)

        this.dracoLoader = new CustomDRACOLoader()
        this.dracoLoader.setDecoderPath('//www.gstatic.com/draco/versioned/decoders/1.4.3/')
        this.dracoLoader.preload()

        this.gltfLoader.setDRACOLoader(this.dracoLoader)

        this.ktx2CheckPromise = Promise.resolve(false)
        //  this.startKtx2Check()
    }

    async testKtx2Support () {
        // test 4x4 ktx image:
        // eslint-disable-next-line max-len
        const bytes = atob('q0tUWCAyMLsNChoKAAAAAAEAAAAEAAAABAAAAAAAAAAAAAAAAQAAAAEAAAABAAAAaAAAACwAAACUAAAAfAAAABABAAAAAAAAgwAAAAAAAACTAQAAAAAAAAEAAAAAAAAAAAAAAAAAAAAsAAAAAAAAAAIAKACjAQIAAwMAAAAAAAAAAAAAAAA/AAAAAAAAAAAA/////xIAAABLVFhvcmllbnRhdGlvbgByZAAAADgAAABLVFh3cml0ZXIAdG9rdHggdjQuMC4wLWJldGE1fjEyIC8gbGlia3R4IHY0LjAuMC1iZXRhNX4zACQAAABLVFh3cml0ZXJTY1BhcmFtcwAtLWJjbXAgLS1jbGV2ZWwgNQABAAEAKwAAAAUAAAArAAAAAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAAAAcAEAAAAAAAA4gOYEyAAAAAAwlr4CAsAJgAAAAAAABABwAQAAAAAAACCBgSICIkCAMFEAAAAAAAA8l8tAJgAAAAAAABACAATAAIAAAAAiAHABAAAAAAAAAIIAAA=')
        const buffer = new ArrayBuffer(bytes.length)
        const bufferView = new Uint8Array(buffer)

        for (let i = 0; i < bytes.length; i += 1) {
            bufferView[i] = bytes.charCodeAt(i)
        }

        const blob = new Blob( [ buffer ], { type: 'image/ktx2' } )
        const objectUrl = URL.createObjectURL(blob)

        return new Promise( ( resolve ) => {
            const onLoad = (texture) => {
                resolve(texture && texture.image && texture.image.width === 4 && texture.image.height === 4)
            }
            const onError = () => {
                resolve(false)
            }
            this.gltfLoader.ktx2Loader.load(objectUrl, onLoad, undefined, onError)
        } )
    }

    startKtx2Check() {
        let tryKtx2 = false
        if (this.gltfLoader.ktx2Loader) {
            const canvas = document.createElement('canvas')
            const webglContext = canvas.getContext('webgl')
            if (webglContext) {
                this.gltfLoader.ktx2Loader.workerConfig = {
                    astcSupported: webglContext.getExtension('WEBGL_compressed_texture_astc') !== null,
                    etc1Supported: webglContext.getExtension('WEBGL_compressed_texture_etc1') !== null,
                    etc2Supported: webglContext.getExtension('WEBGL_compressed_texture_etc') !== null,
                    dxtSupported: webglContext.getExtension('WEBGL_compressed_texture_s3tc') !== null
                        || webglContext.getExtension('MOZ_WEBGL_compressed_texture_s3tc') !== null
                        || webglContext.getExtension('WEBKIT_WEBGL_compressed_texture_s3tc') !== null,
                    bptcSupported: webglContext.getExtension('EXT_texture_compression_bptc') !== null,
                    pvrtcSupported: webglContext.getExtension('WEBGL_compressed_texture_pvrtc') !== null
                        || webglContext.getExtension('WEBKIT_WEBGL_compressed_texture_pvrtc' ) !== null
                }
            }
            tryKtx2 = !!webglContext
        }
        if (tryKtx2) {
            this.ktx2CheckPromise = this.testKtx2Support()
        }
    }

    loadFurniture(url, onProgress, onUpdate, renderer, metrics) {
        renderer.incrementLoadingItems()

        return this.ktx2CheckPromise.then((/* ktx2Supported */) => new Promise((resolve, reject) => {
            const cubeTex = renderer && renderer.cubeTex

            this.gltfLoader.load(url, (gltfstuff) => {
                const furnishing = gltfstuff.scene

                furnishing.traverse((child) => {
                    /* eslint-disable no-param-reassign */
                    if (child instanceof THREE.Mesh) {
                        // this could be used for future material texture overrides
                        if (child.material.transparent /* && child.material.opacity < 0.4 */) {
                            child.castShadow = false
                            child.material.depthWrite = false
                        } else {
                            child.castShadow = true
                        }
                        child.receiveShadow = true

                        // This was introduced as a fix for the simplified items with no hidden faces
                        child.material.shadowSide = renderer.debugSettings.frontSideShadows ? THREE.FrontSide : null

                        if (child.material.map) {
                            // child.material.map.encoding = THREE.LinearEncoding
                            child.material.map.anisotropy = 8
                        }
                        if (child.material.emissiveMap) {
                            // child.material.emissiveMap.encoding = THREE.LinearEncoding
                            child.material.emissiveMap.anisotropy = 8
                        }
                        if (child.material.normalMap) {
                            child.material.normalMap.anisotropy = 8
                        }
                        if (child.material.metalnessMap) {
                            child.material.metalnessMap.anisotropy = 8
                        }
                        if (child.material.aoMap) {
                            child.material.aoMap.anisotropy = 8
                        }
                        if (child.material.alphaMap) {
                            child.material.alphaMap.anisotropy = 8
                        }
                        child.material.needsUpdate = true

                        if (cubeTex) {
                            cubeTex.then((cubetexmap) => {
                                const envMapIntensity = 1.0
                                child.material.envMapIntensity = envMapIntensity
                                child.material.originalEnvMapIntensity = envMapIntensity
                                child.material.envMap = cubetexmap
                                child.material.envMap.isCubeTexture = true
                                child.material.needsUpdate = true
                                child.material.onBeforeCompile = (shader) => { 
                                    Object.defineProperty(child.material, 'envMapRotation',
                                        { 
                                            get: () => shader.uniforms.envMapRotation.value,
                                            set: (val) => { shader.uniforms.envMapRotation.value = val },
                                            configurable: true,
                                        }
                                    )
                                }    
                                onUpdate()
                            })
                        }
                        if (child.geometry) {
                            child.geometry.computeBoundingBox()
                            if (child.geometry.dracoDecompressTime) {
                                metrics.msDracoDecompress += child.geometry.dracoDecompressTime
                            }
                        }
                        child.geometry.computeBoundingBox()
                    }
                })

                furnishing.matrixAutoUpdate = false
                furnishing.castShadow = true
                furnishing.receiveShadow = true

                resolve(furnishing)
            },
                onProgress,
                reject,
                metrics
            )
        }))
        .then((furnishing) => {
            renderer.decrementLoadingItems()
            return furnishing
        })
        .catch((error) => {
            renderer.decrementLoadingItems()
            throw(error)
        })
    }

    dispose() {
        if (this.ktx2Loader && this.ktx2Loader.dispose) {
            this.ktx2Loader.dispose()
        }
        if (this.dracoLoader && this.dracoLoader.dispose) {
            this.dracoLoader.dispose()
        }
    }
}

export default FurnitureLoader
